import {
  WbButton,
  WbCardContent,
  WbMarkdownHelperText,
  WbTablePagination,
  WbTooltip,
  WbWidget,
  useTruncatedStyles,
} from '@agilelab/plugin-wb-platform';
import { useWittyTable } from '@agilelab/plugin-wb-witty-react';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/AddCircle';
import RemoveIcon from '@material-ui/icons/Delete';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import { ArrayFieldTemplateProps } from '@rjsf/utils';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Children,
  Fragment,
  cloneElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createScaffolderLayout } from '../../../layouts';
import { scaffolderPlugin } from '../../../plugin';
import { UI_TABLE_ROW_TEMPLATE_ORIGINAL_TITLE_KEY } from '../../utils';

const DEFAULT_DESCRIPTION_ROWS = 3;

interface TableTemplateProperties {
  maxDescriptionRows: number;
}

function parseTableTemplateProperties(
  props: ArrayFieldTemplateProps,
): TableTemplateProperties {
  const inputMaxDescriptionRows =
    props.uiSchema?.['ui:options']?.maxDescriptionRows;
  const maxDescriptionRows: number =
    inputMaxDescriptionRows &&
    Number.isFinite(inputMaxDescriptionRows) &&
    (inputMaxDescriptionRows as number) > 0
      ? (inputMaxDescriptionRows as number)
      : DEFAULT_DESCRIPTION_ROWS;
  return {
    maxDescriptionRows,
  };
}
const useStyles = makeStyles(theme => ({
  capitalized: {
    textTransform: 'none',
  },
  rowButtonsCell: {
    position: 'sticky',
    right: 0,
    background: theme.palette.white,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderTop: 'none',
    borderRight: 'none',
    padding: '0 12px',
  },
  iconsContainer: {
    display: 'flex',
    justifyContent: 'center',
    columnGap: theme.spacing(1),
    alignItems: 'center',
  },
  divider: {
    height: '20px',
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    margin: '0px 5px',
  },
  table: {
    borderSpacing: 0,
    borderCollapse: 'separate',
  },
  whiteBg: {
    background: theme.palette.white,
  },
  headerCell: {
    padding: '12px 16px',
    textAlign: 'left',
    textTransform: 'uppercase',
    verticalAlign: 'top',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  headerFirstCell: {
    position: 'sticky',
    left: 0,
    padding: '12px 16px',
    textAlign: 'left',
    textTransform: 'uppercase',
    verticalAlign: 'top',
    background: theme.palette.white,
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    zIndex: 1,
  },
  actionsCell: {
    textAlign: 'center',
    position: 'sticky',
    right: 0,
    background: theme.palette.white,
    padding: '12px 16px',
    borderLeft: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
}));

const RowButtons = (props: ArrayFieldTemplateProps['items'][0]) => {
  const witty = useWittyTable();

  const classes = useStyles();
  return (
    <td className={classes.rowButtonsCell}>
      <div className={classes.iconsContainer}>
        {(props.hasMoveUp || props.hasMoveDown) && (
          <IconButton
            color="secondary"
            size="small"
            tabIndex={-1}
            disabled={props.disabled || props.readonly || !props.hasMoveUp}
            onClick={props.onReorderClick(props.index, props.index - 1)}
            style={{ padding: 0 }}
            aria-label="reorder up"
          >
            <KeyboardArrowUp fontSize="small" />
          </IconButton>
        )}

        {(props.hasMoveUp || props.hasMoveDown) && (
          <IconButton
            color="secondary"
            size="small"
            tabIndex={-1}
            disabled={props.disabled || props.readonly || !props.hasMoveDown}
            onClick={props.onReorderClick(props.index, props.index + 1)}
            style={{ padding: 0 }}
            aria-label="reorder down"
          >
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
        )}

        {(props.hasMoveUp || props.hasMoveDown) && (
          <div className={classes.divider} />
        )}

        {props.hasRemove && (
          <IconButton
            size="small"
            tabIndex={-1}
            color="secondary"
            disabled={props.disabled || props.readonly}
            aria-label="reset fields"
            onClick={() => {
              // Reset the witty fields when removing a row so that it triggers all listeners to resubscribe to the WittyTable
              witty?.updateWittyFields({ type: 'RESET_FIELDS' });
              props.onDropIndexClick(props.index)();
            }}
          >
            <WbTooltip title="Remove Row" placement="bottom-start">
              <RemoveIcon fontSize="small" />
            </WbTooltip>
          </IconButton>
        )}
      </div>
    </td>
  );
};

interface ColumnHeader {
  property: string;
  title: string;
  required: boolean;
  description?: string;
}

/**
 * Extracts the column headers form the input properties.
 * The column headers are sorted in the same way they are defined in the schema.
 * @param props a complex object containing all the properties definitions, schema, etc
 * @returns a map containing for each property an object that represents it.
 */
function extractColumnHeaders(
  props: ArrayFieldTemplateProps,
): Map<string, ColumnHeader> {
  const distinctColumns = new Map<string, ColumnHeader>();

  // get the required properties. We do not extract the conditional ones since they can be misleading for the rows that do not have such value
  const requiredFields = (props.schema.items as any).required;

  // extract all the properties (even the conditional ones) sorted depending on how they are defined in the schema
  const sortedKeys = Object.keys(props.idSchema).reduce(
    (m, e, i) => m.set(e, i),
    new Map(),
  );

  // we need to show only the columns that have at least one value.
  // the conditional ones are a problem since they can be rendered only if we have at least one row for which they need to be displayed.
  if (props.items.reduce((x, y) => x + Children.count(y.children), 0) === 0) {
    // if there are no rows (children) we can display only the defined ones
    const tableRowItems = (props.schema.items as any)?.properties;
    if (tableRowItems) {
      Object.keys(tableRowItems).forEach(itemName => {
        if (!distinctColumns.has(itemName)) {
          distinctColumns.set(itemName, {
            property: itemName,
            title: tableRowItems[itemName].title,
            required: requiredFields.includes(itemName),
            description: tableRowItems[itemName].description,
          });
        }
      });
    }
  } else {
    // if there are children, we need to display the union of all the columns of all the rows
    props.items.forEach(item =>
      Children.forEach(item.children, child => {
        const childProperties = child.props.schema.properties;
        if (childProperties) {
          Object.keys(childProperties).forEach(property => {
            if (!distinctColumns.has(property)) {
              distinctColumns.set(property, {
                property: property,
                title: childProperties[property].title,
                required: requiredFields.includes(property),
                description: childProperties[property].description,
              });
            }
          });
        }
      }),
    );
  }

  // since the columns can be added to the map in the wrong order (when they are extracted from the children)
  // we need to sort them using the default properties sorting we extracted
  const sortedDistinctColumns = new Map(
    [...distinctColumns.entries()].sort(
      ([keyA, _], [keyB]) => sortedKeys.get(keyA) - sortedKeys.get(keyB),
    ),
  );

  return sortedDistinctColumns;
}
export const ArrayTableContent = (
  props: ArrayFieldTemplateProps & { RowButton?: JSX.Element },
) => {
  const classes = useStyles();
  const { maxDescriptionRows } = parseTableTemplateProperties(props);
  const rowClasses = useTruncatedStyles({ maxLines: maxDescriptionRows });
  const columnHeaders = extractColumnHeaders(props);
  const [page, setPage] = useState(0);
  const configApi = useApi(configApiRef);
  const configuredRowsPerPage = configApi.getOptionalNumber(
    'mesh.builder.scaffolder.arrayFields.rowsPerPage',
  );

  const rowsPerPage =
    configuredRowsPerPage &&
    Number.isFinite(configuredRowsPerPage) &&
    configuredRowsPerPage > 0
      ? Math.floor(configuredRowsPerPage)
      : undefined;

  const totalRows = props.items.length;
  const previousTotalRowsRef = useRef(totalRows);

  useEffect(() => {
    const previousTotalRows = previousTotalRowsRef.current;

    if (!rowsPerPage) {
      if (page !== 0) {
        setPage(0);
      }
      previousTotalRowsRef.current = totalRows;
      return;
    }

    const lastPage = Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);

    if (totalRows > previousTotalRows) {
      setPage(prevPage => (prevPage === lastPage ? prevPage : lastPage));
    } else if (page > lastPage) {
      setPage(lastPage);
    }

    previousTotalRowsRef.current = totalRows;
  }, [rowsPerPage, totalRows, page]);

  const pagedItems = rowsPerPage
    ? props.items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : props.items;

  return (
    <WbCardContent style={{ overflow: 'auto', padding: 0 }}>
      {props.items.length > 0 && (
        <div style={{ overflow: 'auto' }}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.whiteBg}>
                {Array.from(columnHeaders.values()).map((header, index) => (
                  <TableCell
                    key={index}
                    className={
                      index === 0 ? classes.headerFirstCell : classes.headerCell
                    }
                  >
                    <Typography
                      style={{ fontWeight: 500 }}
                      variant="caption"
                      color="primary"
                    >
                      {header.title} {header.required ? '*' : ''}
                    </Typography>
                    {header.description && (
                      <WbTooltip
                        placement="bottom-start"
                        title={
                          <Fragment>
                            <WbMarkdownHelperText
                              helperText={header.description}
                            />
                          </Fragment>
                        }
                      >
                        <div>
                          <WbMarkdownHelperText
                            className={`${classes.capitalized} ${rowClasses.truncated}`}
                            helperText={header.description}
                          />
                        </div>
                      </WbTooltip>
                    )}
                  </TableCell>
                ))}

                <TableCell className={classes.actionsCell}>
                  <Typography
                    style={{ fontWeight: 500 }}
                    variant="caption"
                    color="secondary"
                  >
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedItems.map(item => (
                <Fragment key={item.key}>
                  {Children.map(item.children, child =>
                    cloneElement(child, {
                      schema: {
                        ...child.props.schema,
                        columnHeaders: columnHeaders,
                        RowButtons: (
                          <RowButtons {...item} disabled={props.disabled} />
                        ),
                      },
                      disabled: props.disabled,
                    }),
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {props.items.length > 0 && rowsPerPage && (
        <WbTablePagination
          component="div"
          count={totalRows}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPageOptions={[]}
        />
      )}
    </WbCardContent>
  );
};

export const ArrayTableTemplate = (props: ArrayFieldTemplateProps) => {
  return (
    <WbWidget
      title={
        props.uiSchema?.['ui:title'] ||
        // When in a TableRowTemplate, we want to show the original title of the field
        props.uiSchema?.[UI_TABLE_ROW_TEMPLATE_ORIGINAL_TITLE_KEY] ||
        props.title
      }
      actions={
        props.canAdd ? (
          <WbButton
            label="Add"
            disabled={!props.canAdd}
            icon={<AddIcon />}
            onClick={props.onAddClick}
            color="secondary"
          />
        ) : undefined
      }
    >
      <ArrayTableContent {...props} />
    </WbWidget>
  );
};

export const ArrayTableTemplateLayout = scaffolderPlugin.provide(
  createScaffolderLayout({
    name: 'ArrayTableTemplate',
    component: ArrayTableTemplate as any,
  }),
);
